import { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import toast from 'react-hot-toast';
import {
  Description as FileTextIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ClockIcon,
  MoreVert as MoreVertIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';

const DocumentCard = ({
  document,
  onSendEmail,
  onViewPdf,
  onGeneratePDF,
  loading = false,
  pdfLoading = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const isEmailSent = document?.email_sent_at !== null;
  const hasPDF =
    document?.pdf_link !== null && document?.pdf_link !== undefined;
  const documentName =
    document?.audience === 'client' ? 'Client Document' : 'Freelancer Document';

  const handleViewPdf = () => {
    if (onViewPdf && document?.pdf_link) {
      onViewPdf(document.pdf_link);
    } else if (document?.pdf_link) {
      window.open(document.pdf_link, '_blank');
    }
  };

  const handleSendEmail = () => {
    if (onSendEmail && document?.id) {
      onSendEmail(document.id);
    }
    setAnchorEl(null);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleGeneratePDF = () => {
    if (onGeneratePDF) {
      onGeneratePDF();
    }
    setAnchorEl(null);
  };

  const handleViewPdfFromMenu = () => {
    handleViewPdf();
    setAnchorEl(null);
  };

  const formatEmailSentDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        border: '1px solid',
        borderColor: 'divider',
        flex: '1 1 300px',
        maxWidth: 400,
        minWidth: 300,
        padding: 3,
      }}
    >
      <Box>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='start'
          marginBottom={2}
        >
          <Box display='flex' alignItems='center' gap={2}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1.5,
                bgcolor: '#FEF2F2',
                color: '#DC2626',
              }}
            >
              <FileTextIcon />
            </Avatar>
            <Box>
              <Box
                display='flex'
                alignItems='center'
                gap={0.5}
                mb={0.5}
              >
                <Typography variant='subtitle1' fontWeight={600}>
                  {documentName}
                </Typography>
                {document?.pdf_link && (
                  <Tooltip title='Copy document URL'>
                    <IconButton
                      size='small'
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard
                          .writeText(document.pdf_link)
                          .then(() => {
                            toast.success('Document URL copied to clipboard');
                          });
                      }}
                      sx={{
                        p: 0.25,
                        color: 'text.secondary',
                        '&:hover': {
                          color: 'primary.main',
                          backgroundColor: 'action.hover',
                        },
                      }}
                      aria-label='Copy document URL to clipboard'
                    >
                      <ContentCopyIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box display='flex' alignItems='center' gap={1}>
                <Chip
                  label={`v${document?.version || 1}`}
                  size='small'
                  variant='outlined'
                  sx={{ fontSize: '0.75rem', height: 24 }}
                />
                <Chip
                  label={document?.audience || 'Unknown'}
                  size='small'
                  variant='filled'
                  color='primary'
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    textTransform: 'capitalize',
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Actions Menu */}
          <IconButton
            onClick={handleMenuClick}
            size='small'
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        {/* Email Status */}
        {/* <Box display='flex' alignItems='center' gap={0.5}>
          {isEmailSent && document?.email_sent_at ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 14, color: '#475569' }} />
              <Typography variant='caption' color='text.secondary'>
                Sent on {formatEmailSentDate(document.email_sent_at)}
              </Typography>
            </>
          ) : (
            <>
              <ClockIcon sx={{ fontSize: 14, color: '#475569' }} />
              <Typography variant='caption' color='text.secondary'>
                Pending
              </Typography>
            </>
          )}
        </Box> */}
      </Box>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              minWidth: 200,
              mt: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Show Generate PDF if no PDF exists */}
        {!hasPDF && (
          <MenuItem onClick={handleGeneratePDF} disabled={pdfLoading}>
            <ListItemIcon>
              <FileTextIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText>
              {pdfLoading ? 'Generating PDF...' : 'Generate PDF'}
            </ListItemText>
          </MenuItem>
        )}

        {/* Show PDF actions if PDF exists */}
        {hasPDF && (
          <>
            <MenuItem onClick={handleViewPdfFromMenu}>
              <ListItemIcon>
                <OpenInNewIcon fontSize='small' />
              </ListItemIcon>
              <ListItemText>View PDF</ListItemText>
            </MenuItem>

            {!isEmailSent && (
              <MenuItem onClick={handleSendEmail} disabled={loading}>
                <ListItemIcon>
                  <SendIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>Send Email</ListItemText>
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </Box>
  );
};

export default DocumentCard;
